/**
 * Shared primitives for the compliance workspaces (AI Act, GDPR, DORA, NIS2).
 *
 * Kept generic so each workspace can compose them without duplicating UI logic.
 */
import React, { useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Clock } from "lucide-react";

/**
 * Tabs primitive: horizontal navy/ivory tabs (mirrors WatchdogsAlerts).
 * Props: { tabs: [{key, label, badge?}], active, onChange, testIdPrefix }
 */
export function WorkspaceTabs({ tabs, active, onChange, testIdPrefix }) {
    return (
        <div
            className="inline-flex flex-wrap border border-[#0D1B3E]/20 bg-white"
            data-testid={`${testIdPrefix}-tabs`}
        >
            {tabs.map((t) => (
                <button
                    key={t.key}
                    type="button"
                    onClick={() => onChange(t.key)}
                    data-testid={`${testIdPrefix}-tab-${t.key}`}
                    className={`px-4 py-2.5 text-xs uppercase tracking-[0.18em] border-r last:border-r-0 border-[#0D1B3E]/20 transition-colors ${
                        active === t.key
                            ? "bg-[#0D1B3E] text-[#F5F0E8]"
                            : "bg-transparent text-[#0D1B3E]/75 hover:text-[#0D1B3E]"
                    }`}
                >
                    {t.label}
                    {t.badge != null && (
                        <span className="ml-2 text-[10px] opacity-75">({t.badge})</span>
                    )}
                </button>
            ))}
        </div>
    );
}

/**
 * Decision-tree primitive. Walks the user through a flow defined as:
 *   { questions: [{id, prompt, help?, options: [{label, next}]}],
 *     outcomes: { key: { label, body, ...extras } } }
 *
 * `next` is either 'outcome:<key>' or a question id. The component renders
 * the current question, lets the user pick, and reveals the outcome.
 */
export function DecisionTree({ tree, testIdPrefix, renderOutcome }) {
    const [history, setHistory] = useState([]); // array of {id, choice}
    const [outcomeKey, setOutcomeKey] = useState(null);

    const startId = tree.questions?.[0]?.id;
    const currentId =
        history.length === 0 ? startId : history[history.length - 1].nextId;
    const current = outcomeKey
        ? null
        : tree.questions.find((q) => q.id === currentId);

    const choose = (qId, idx, next) => {
        if (next.startsWith("outcome:")) {
            setHistory((h) => [...h, { id: qId, choice: idx, nextId: null }]);
            setOutcomeKey(next.slice("outcome:".length));
        } else {
            setHistory((h) => [...h, { id: qId, choice: idx, nextId: next }]);
        }
    };
    const back = () => {
        setOutcomeKey(null);
        setHistory((h) => h.slice(0, -1));
    };
    const reset = () => {
        setHistory([]);
        setOutcomeKey(null);
    };

    if (outcomeKey) {
        const outcome = tree.outcomes?.[outcomeKey];
        return (
            <div data-testid={`${testIdPrefix}-outcome`}>
                {renderOutcome ? (
                    renderOutcome(outcomeKey, outcome, { reset, back })
                ) : (
                    <DefaultOutcomeCard
                        outcome={outcome}
                        outcomeKey={outcomeKey}
                        testIdPrefix={testIdPrefix}
                    />
                )}
                <div className="mt-6 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={back}
                        className="btn-secondary"
                        data-testid={`${testIdPrefix}-back-btn`}
                    >
                        <ArrowLeft size={14} /> Back
                    </button>
                    <button
                        type="button"
                        onClick={reset}
                        className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C]"
                        data-testid={`${testIdPrefix}-reset-btn`}
                    >
                        Start over
                    </button>
                </div>
            </div>
        );
    }

    if (!current) return null;
    return (
        <div data-testid={`${testIdPrefix}-question-${current.id}`}>
            <div className="label-eyebrow">
                Question {history.length + 1}
            </div>
            <h3 className="mt-2 font-display text-2xl tracking-tight">
                {current.prompt}
            </h3>
            {current.help && (
                <p className="mt-2 text-sm text-[#0D1B3E]/65">{current.help}</p>
            )}
            <ul className="mt-5 space-y-2">
                {current.options.map((opt, idx) => (
                    <li key={idx}>
                        <button
                            type="button"
                            onClick={() => choose(current.id, idx, opt.next)}
                            className="w-full text-left card-flat p-4 hover:border-[#C9A84C] flex items-center justify-between gap-4 transition-colors"
                            data-testid={`${testIdPrefix}-opt-${current.id}-${idx}`}
                        >
                            <span className="text-sm text-[#0D1B3E]/90 leading-relaxed">
                                {opt.label}
                            </span>
                            <ArrowRight
                                size={16}
                                className="text-[#0D1B3E]/40 shrink-0"
                            />
                        </button>
                    </li>
                ))}
            </ul>
            {history.length > 0 && (
                <div className="mt-5">
                    <button
                        type="button"
                        onClick={back}
                        className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C] flex items-center gap-1"
                        data-testid={`${testIdPrefix}-back-question`}
                    >
                        <ArrowLeft size={14} /> Back one step
                    </button>
                </div>
            )}
        </div>
    );
}

function DefaultOutcomeCard({ outcome, outcomeKey, testIdPrefix }) {
    const tone = outcome?.tone || "info";
    const toneCls =
        tone === "danger"
            ? "bg-rose-50 border-rose-300 text-rose-900"
            : tone === "warning"
            ? "bg-amber-50 border-amber-300 text-amber-900"
            : tone === "success"
            ? "bg-emerald-50 border-emerald-300 text-emerald-900"
            : "bg-sky-50 border-sky-300 text-sky-900";
    return (
        <div
            className={`card-flat border-2 p-7 ${toneCls}`}
            data-testid={`${testIdPrefix}-outcome-${outcomeKey}`}
        >
            <div className="label-eyebrow">Outcome</div>
            <h3 className="mt-2 font-display text-2xl tracking-tight">
                {outcome?.label}
            </h3>
            {outcome?.body && (
                <p className="mt-3 leading-relaxed text-sm">{outcome.body}</p>
            )}
            {outcome?.summary && (
                <p className="mt-3 leading-relaxed text-sm">{outcome.summary}</p>
            )}
            {Array.isArray(outcome?.next_steps) && outcome.next_steps.length > 0 && (
                <div className="mt-5">
                    <div className="label-eyebrow">Next steps</div>
                    <ul className="mt-2 space-y-1.5 text-sm">
                        {outcome.next_steps.map((s, i) => (
                            <li key={i} className="flex gap-2">
                                <span>–</span>
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

/**
 * Checklist primitive: grouped items with progress bar.
 * Props: { groups: [{title, items: [{id, text}]}], storageKey?, testIdPrefix }
 */
export function Checklist({ groups, storageKey, testIdPrefix }) {
    const [checked, setChecked] = useState(() => {
        if (storageKey && typeof window !== "undefined") {
            try {
                return JSON.parse(localStorage.getItem(storageKey) || "{}");
            } catch {
                return {};
            }
        }
        return {};
    });

    const allItems = groups.flatMap((g) => g.items);
    const total = allItems.length;
    const done = allItems.filter((i) => checked[i.id]).length;
    const pct = total ? Math.round((done / total) * 100) : 0;

    const toggle = (id) => {
        setChecked((c) => {
            const next = { ...c, [id]: !c[id] };
            if (storageKey && typeof window !== "undefined") {
                try {
                    localStorage.setItem(storageKey, JSON.stringify(next));
                } catch {
                    /* noop */
                }
            }
            return next;
        });
    };

    return (
        <div data-testid={`${testIdPrefix}-checklist`}>
            <div className="card-flat p-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow">Readiness</div>
                    <div className="mt-1 font-display text-3xl tracking-tight">
                        {pct}%{" "}
                        <span className="text-sm text-[#0D1B3E]/60 font-normal">
                            ({done} / {total})
                        </span>
                    </div>
                </div>
                <div className="w-full max-w-xs">
                    <div className="h-2 bg-[#0D1B3E]/10">
                        <div
                            className="h-full bg-[#C9A84C] transition-all"
                            style={{ width: `${pct}%` }}
                            data-testid={`${testIdPrefix}-progress`}
                        />
                    </div>
                </div>
            </div>

            <div className="mt-6 space-y-8">
                {groups.map((g, gi) => (
                    <section key={gi}>
                        <h3 className="font-display text-xl tracking-tight">
                            {g.title}
                        </h3>
                        <ul className="mt-4 space-y-2">
                            {g.items.map((it) => {
                                const ok = !!checked[it.id];
                                return (
                                    <li
                                        key={it.id}
                                        className="card-flat p-4 flex items-start gap-3"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggle(it.id)}
                                            data-testid={`${testIdPrefix}-item-${it.id}`}
                                            className="shrink-0 mt-0.5"
                                            aria-pressed={ok}
                                        >
                                            {ok ? (
                                                <CheckCircle2
                                                    size={20}
                                                    color="#C9A84C"
                                                />
                                            ) : (
                                                <Circle
                                                    size={20}
                                                    className="text-[#0D1B3E]/30"
                                                />
                                            )}
                                        </button>
                                        <span
                                            className={`text-sm leading-relaxed ${
                                                ok
                                                    ? "text-[#0D1B3E]/55 line-through"
                                                    : "text-[#0D1B3E]/90"
                                            }`}
                                        >
                                            {it.text}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                ))}
            </div>
        </div>
    );
}

/**
 * Penalty calculator: interactive band display.
 * Props: { bands: [{category, max_fixed_eur, max_pct_turnover, rule, examples, note?}], testIdPrefix }
 *
 * Lets the user enter a global annual turnover to see the worst-case fine for
 * each band (using the 'max' or 'min' rule from the band).
 */
export function PenaltyCalculator({ bands, testIdPrefix }) {
    const [turnover, setTurnover] = useState(""); // EUR
    const t = parseFloat((turnover || "0").replace(/,/g, "")) || 0;

    const fmt = (n) =>
        n == null
            ? "–"
            : new Intl.NumberFormat("en-IE", {
                  style: "currency",
                  currency: "EUR",
                  maximumFractionDigits: 0,
              }).format(n);

    return (
        <div data-testid={`${testIdPrefix}-penalty-calc`}>
            <div className="card-flat p-6">
                <label className="label-eyebrow">
                    Global annual turnover (EUR)
                </label>
                <input
                    type="text"
                    inputMode="numeric"
                    className="field mt-2"
                    placeholder="e.g. 500000000"
                    value={turnover}
                    onChange={(e) =>
                        setTurnover(e.target.value.replace(/[^0-9.,]/g, ""))
                    }
                    data-testid={`${testIdPrefix}-turnover-input`}
                />
                <p className="mt-2 text-xs text-[#0D1B3E]/60">
                    Enter your group&apos;s worldwide annual turnover to see the
                    worst-case administrative fine.
                </p>
            </div>

            <div className="mt-6 space-y-4">
                {bands.map((b, i) => {
                    const fixed = b.max_fixed_eur ?? null;
                    const pct = b.max_pct_turnover ?? null;
                    const pctEur = pct != null ? (t * pct) / 100 : null;
                    let max = null;
                    if (b.rule === "max") {
                        const candidates = [fixed, pctEur].filter(
                            (v) => v != null
                        );
                        max = candidates.length ? Math.max(...candidates) : null;
                    } else if (b.rule === "min") {
                        const candidates = [fixed, pctEur].filter(
                            (v) => v != null
                        );
                        max = candidates.length ? Math.min(...candidates) : null;
                    }
                    return (
                        <div
                            key={i}
                            className="card-flat p-6"
                            data-testid={`${testIdPrefix}-band-${i}`}
                        >
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div>
                                    <div className="label-eyebrow">
                                        {b.rule === "max"
                                            ? "Higher of"
                                            : b.rule === "min"
                                            ? "Lower of"
                                            : "–"}
                                    </div>
                                    <h4 className="mt-1 font-display text-xl tracking-tight">
                                        {b.category}
                                    </h4>
                                    {b.note && (
                                        <p className="mt-2 text-xs text-[#0D1B3E]/60">
                                            {b.note}
                                        </p>
                                    )}
                                </div>
                                <div className="text-right">
                                    <div className="label-eyebrow">
                                        Max fine
                                    </div>
                                    <div className="mt-1 font-display text-2xl tracking-tight text-[#C9A84C]">
                                        {fmt(max)}
                                    </div>
                                    <div className="text-[11px] text-[#0D1B3E]/55 uppercase tracking-[0.18em]">
                                        {fixed != null && (
                                            <>fixed {fmt(fixed)} </>
                                        )}
                                        {pct != null && <>· {pct}% of turnover</>}
                                    </div>
                                </div>
                            </div>
                            {b.examples?.length > 0 && (
                                <ul className="mt-4 space-y-1 text-xs text-[#0D1B3E]/70">
                                    {b.examples.map((e, j) => (
                                        <li key={j} className="flex gap-2">
                                            <span>–</span>
                                            <span>{e}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Timeline primitive: vertical timeline with date + body.
 * Props: { items: [{label, deadline, body}], testIdPrefix }
 */
export function StepTimeline({ items, testIdPrefix }) {
    return (
        <ol
            className="relative border-l-2 border-[#C9A84C]/40 ml-3 space-y-7"
            data-testid={`${testIdPrefix}-timeline`}
        >
            {items.map((it, i) => (
                <li
                    key={i}
                    className="pl-7 relative"
                    data-testid={`${testIdPrefix}-timeline-${i}`}
                >
                    <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#C9A84C] border-4 border-[#F5F0E8]" />
                    <div className="label-eyebrow flex items-center gap-2">
                        <Clock size={11} /> {it.deadline}
                    </div>
                    <div className="font-display text-xl tracking-tight mt-1">
                        {it.label}
                    </div>
                    <p className="mt-2 text-sm text-[#0D1B3E]/80 leading-relaxed">
                        {it.body}
                    </p>
                </li>
            ))}
        </ol>
    );
}

/**
 * Workspace page header: used by all four pages for visual consistency.
 */
export function WorkspaceHero({ eyebrow, title, subtitle, badges }) {
    return (
        <div>
            <div className="label-eyebrow">{eyebrow}</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">
                {title}
            </h1>
            {subtitle && (
                <p className="mt-3 max-w-2xl text-[#0D1B3E]/75">{subtitle}</p>
            )}
            {Array.isArray(badges) && badges.length > 0 && (
                <div className="mt-5 flex items-center gap-2 flex-wrap">
                    {badges.map((b, i) => (
                        <span
                            key={i}
                            className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-[#0D1B3E]/20 text-[#0D1B3E]/70"
                        >
                            {b}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
